// app/src/features/share/ShareHost.jsx
import React from "react";
import ShareDialog from "../../components/ShareDialog.jsx";
import { sharesChanged } from "../../lib/forecast/reducer.js";
import { recordUiEvent } from "../../lib/logRingBuffer";

/**
 * The share dialog, the sole content of `AppFrame`'s `shareDialog` slot (desktop-only — the frame
 * renders the slot on its desktop branch alone, first in its fragment) exactly as the inline mount
 * sat. Mounted only while open, so a fresh open is a fresh edit session.
 *
 * `ctx` is the frame context: the dialog gate's `shareDialogOpen` / `closeShareDialog` /
 * `shareTarget`, the auth session's `org`, the two owner-only reads (`forecastShares`,
 * `shareCandidates`) and `dispatchRun` — the store's dispatch, injected (a `features/` file may
 * not import the store), which `onSave` calls with `sharesChanged` where `AppInner` called
 * `runStore.dispatch` (the same function). The host hands `ctx` nowhere.
 */
export default function ShareHost({ ctx }) {
  const {
    shareDialogOpen,
    closeShareDialog,
    shareTarget,
    org,
    forecastShares,
    shareCandidates,
    dispatchRun,
  } = ctx;
  return (
    <>
      {/* ShareDialog — mounted ONLY while open (same pattern SendCopyModal
          uses: {sendCopyOpen && <SendCopyModal .../>}), so a fresh mount is a
          fresh edit session with no stale local selection to reset by hand
          (ShareDialog.jsx's own header). `shareDialogOpen` already ANDs the
          gate (useShareDialogGate), so this needs no second guard. */}
      {shareDialogOpen && (
        <ShareDialog
          onClose={closeShareDialog}
          isOwn={shareTarget?.isOwn}
          // Matches the `?forecast=<server uuid>` in the address bar that installUrlSync mirrors,
          // built from the CURRENT location origin.
          forecastUrl={
            shareTarget?.forecastId
              ? `${window.location.origin}${window.location.pathname}?forecast=${shareTarget.forecastId}`
              : null
          }
          orgName={org?.name ?? null}
          orgId={org?.id ?? null}
          loading={forecastShares.loading}
          shares={forecastShares.shares}
          candidatesLoading={shareCandidates.loading}
          teams={shareCandidates.teams}
          people={shareCandidates.people}
          saving={forecastShares.saving}
          error={forecastShares.error}
          onSave={async (desired) => {
            // LANE COUNTS, NEVER THE SUBJECTS. A bundle must not carry who a
            // forecast is shared with — that is other people's identity, and
            // the subject grammar deliberately never crosses the wire in
            // either direction. Counts answer the question a triager actually
            // has ("what did they submit, and did it stick") without naming
            // anyone. `total: 0` is the revoke-everything case and is exactly
            // the submission most worth seeing in a report.
            const byLane = { org: 0, team: 0, user: 0 };
            for (const d of desired) if (d?.type in byLane) byLane[d.type] += 1;
            recordUiEvent({
              event: "ui.share.save",
              forecastId: shareTarget?.forecastId || null,
              total: desired.length,
              ...byLane,
            });
            const ok = await forecastShares.save(desired);
            if (!ok) {
              recordUiEvent({
                event: "ui.share.save_failed",
                forecastId: shareTarget?.forecastId || null,
              });
            }
            // Keep the SHARE control honest without a reload: `isShared` rides the LIST fetch,
            // which nothing re-fetches on save. Derive it from what we just submitted, not from a
            // re-read: the write is a whole-set replace, so a non-empty desired set IS the new
            // state. Only on success — a failed save must not repaint the button as if it worked.
            // Stamped on the forecast that was SAVED, not the focused one, because the dialog
            // manages whichever row it was opened from.
            if (ok && shareTarget?.id) {
              dispatchRun(sharesChanged(shareTarget.id, desired.length > 0));
            }
            return ok;
          }}
        />
      )}
    </>
  );
}
